import { segmentoRfmFromString } from '@/models/enums/SegmentoRfm';
import JwtAuthToken from '@/models/JwtAuthToken';

/** Extrai do JSON de featureFlags do JWT apenas chaves que começam com `feat_`. */
function extrairFeatureFlagsDoJwt(featureFlagsJson: string | undefined): Record<string, unknown> {
  if (!featureFlagsJson?.trim()) {
    return {};
  }
  try {
    const parsed = JSON.parse(featureFlagsJson) as unknown;
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(([key]) => key.startsWith('feat_'))
    );
  } catch {
    return {};
  }
}

let isInitialized = false;

const adoptlyService = {
  init(token: JwtAuthToken) {
    try {
      if (isInitialized) {
        return;
      }

      const projectId = appSettings.VUE_APP_ADOPTLY_PROJECT_ID;
      if (!projectId) {
        return;
      }

      const { $adoptly } = window;
      if (!Array.isArray($adoptly)) {
        return;
      }

      const contactKey = token.payload.hash;
      const firstName = token.primeiroNome || '';
      const featureFlags = extrairFeatureFlagsDoJwt(token.payload.featureFlags);
      const segmentoRfm = segmentoRfmFromString(token.payload.segmentoRfm);

      $adoptly.push([
        'init',
        projectId,
        'pt',
        {
          username: contactKey,
          firstname: firstName,
          // ADOPTLY-EMAIL: campo extra suportado pelo Adoptly;
          // o JWT só expõe o e-mail ofuscado no frontend, não há e-mail completo disponível aqui.
          email: token.payload.emailOfuscado,
          props: {
            segmentoRfm,
            ...featureFlags,
          },
        },
        {
          disableLocationChangeDetection: true,
          environment: appSettings.VUE_APP_ADOPTLY_ENV,
          piiConsent: true,
        },
      ]);
      isInitialized = true;
    } catch (error) {
      console.warn('Erro ao inicializar Adoptly:', error);
    }
  },

  pageChanged() {
    try {
      const { adoptly } = window;
      if (typeof adoptly?.pageChanged !== 'function') {
        return;
      }

      adoptly.pageChanged();
    } catch (error) {
      console.warn('Erro ao notificar mudança de página no Adoptly:', error);
    }
  },

  /** Engine carregado ou fila `$adoptly` presente: sem nenhum dos dois, o evento não teria destino. */
  disponivel(): boolean {
    return typeof window.adoptly?.track === 'function' || Array.isArray(window.$adoptly);
  },

  trackEvent(
    eventName: string,
    attributes?: Record<string, unknown>,
    options?: { data?: unknown },
    { enfileirarSeIndisponivel = false } = {}
  ) {
    try {
      const { adoptly } = window;
      if (typeof adoptly?.track !== 'function') {
        // Evento de envio único: antes do engine.js carregar, vai pela fila, que ele drena ao carregar.
        if (enfileirarSeIndisponivel && Array.isArray(window.$adoptly)) {
          window.$adoptly.push(['track', eventName, attributes ?? {}, options ?? {}]);
        }
        return;
      }

      if (options === undefined) {
        adoptly.track(eventName, attributes ?? {});
        return;
      }
      adoptly.track(eventName, attributes ?? {}, options);
    } catch (error) {
      // Só a mensagem: `options` pode levar dados financeiros do consumidor.
      console.warn('Erro ao enviar evento customizado no Adoptly:', error instanceof Error ? error.message : 'erro');
    }
  },
};

/** Reseta a flag de inicialização. Apenas para uso em testes. */
export function resetAdoptlyInitializedFlag() {
  isInitialized = false;
}

export default adoptlyService;
